import React, { ReactElement, useContext, useMemo } from 'react';
import HeaderContainer from '../connectedForm/HeaderContainer';
import SalesOrderHeader from './SalesOrderHeader';
import HeaderAction from '../../components/connectedForm/HeaderAction';
import { DangerButton, SecondaryButton } from '../connectedForm/Button';
import { useSalesOrderFormActions } from '../../actions/sales/SalesOrderAction';
import styled from 'styled-components';
import BreadCrumbs, { IBreadCrumbsProps } from '../connectedForm/BreadCrumbs';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { SAVE_ATTACHMENT_OPTIONS, ISelectButtonProps, ISubmitOptionEntry, SaveSelectButton } from '../SelectButton';
// @ts-ignore
import { Analytics } from 'mixpanel';
import ActionButtons from '../connectedForm/ActionButtons';
import { IAlert, ISalesOrder } from '../../graphql/schema/p4types';
import { HeaderNotification } from '../Notification/HeaderNotification';
import { useDeleteRecord } from '../connectedForm/ConnectedHeader';
import { IEditableDataGridViewController } from '../view/UseEditableDataGridViewController';
import { useDispatch } from 'react-redux';
import { IStatusFlag } from '../connectedForm/HeaderBlock/HeaderBlock';
import { SubmitOption } from '../../@types/connectedFormTypes';
import { usePristineEntity } from '../connectedForm/Hooks/pristineEntityHook';

const FlexDiv = styled.div`
  display: flex;
`;

const SalesOrderHeaderComponent = (
  props: {
    viewController: IEditableDataGridViewController;
    noDeleteOption: boolean;
    noSaveOption: boolean;
    readOnly: boolean;
    match: { params?: any };
    deletePermitted: boolean;
    values: ISalesOrder;
    statusFlags: IStatusFlag | Array<IStatusFlag>;
    orderNumber: number | string;
    onCancel: () => void;
  } & IBreadCrumbsProps
) => {
  const {
    noDeleteOption,
    noSaveOption,
    readOnly,
    match,
    deletePermitted,
    values: salesOrder,
    statusFlags,
    orderNumber,
    onCancel,
  } = props;
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();

  const handleSubmit = () => {
    connectedForm.submit?.();
    Analytics.track('Sales order saved', {
      orderTotal: salesOrder?.baseGrossGoodsTotal,
      numberOfLines: salesOrder?.orderLines?.length ?? 0,
    });
  };
  const handleCancel = () => onCancel();

  const [handleDelete, deleteModals] = useDeleteRecord({
    archive: true,
  });

  const field = salesOrder?.orderType || 'ORDER';
  const customer = salesOrder?.customer?.name;
  const [attachmentsPristine] = usePristineEntity({ values: props.values?.attachments });

  const deleteButton =
    noDeleteOption || !deletePermitted || (match && match.params.id === 'New') ? null : (
      <DangerButton onClick={handleDelete}>Archive</DangerButton>
    );

  const SALES_ORDER_SAVE_OPTIONS = useMemo<ISelectButtonProps['options']>(
    () => [
      {
        value: SubmitOption.Exit,
        label: `Save ${field.toLowerCase()}`,
      },
      {
        value: SubmitOption.Continue,
        label: `Save ${field.toLowerCase()} & continue`,
      },
      {
        value: SubmitOption.New,
        label: `Save ${field.toLowerCase()} & add new`,
        condition: (params) => `${params.id || params.uuid}`.toLowerCase() === 'new',
      },
    ],
    [field]
  );

  const successButton = noSaveOption ? null : (
    <SaveSelectButton
      onClick={handleSubmit}
      saveOptions={readOnly && !attachmentsPristine ? SAVE_ATTACHMENT_OPTIONS : SALES_ORDER_SAVE_OPTIONS}
      saveReadOnlyCondition={(defaultReadOnly) => {
        if (attachmentsPristine) return defaultReadOnly;
        return false;
      }}
    />
  );

  const [standardActionButtons, modals] = useSalesOrderFormActions();
  const actionButtons = useMemo(
    () => [...standardActionButtons, ...props.viewController.actions],
    [props.viewController.actions, standardActionButtons]
  );

  const headerAlerts: Array<IAlert> = connectedForm.headerAlerts ?? [];
  const headerAlertElements: Array<ReactElement> = headerAlerts.map((alert) => {
    return <HeaderNotification key={alert.uuid as string} alert={alert} />;
  });

  return (
    <>
      {modals}
      {deleteModals}
      <BreadCrumbs {...props} title={connectedForm?.entity?.title === 'Quotes' ? 'Quote' : 'Order'} />
      <HeaderContainer>
        <SalesOrderHeader optValue={customer} orderNumber={orderNumber} statusFlags={statusFlags} values={salesOrder} />
        <HeaderAction>
          <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>
          {salesOrder ? (
            <FlexDiv key="sales_order_header_key">
              {!!salesOrder.id ? deleteButton : null}
              <ActionButtons actionButtons={actionButtons} />
            </FlexDiv>
          ) : null}
          {successButton}
        </HeaderAction>
      </HeaderContainer>
      {headerAlertElements?.length > 0 ? (
        <div
          style={{
            backgroundColor: '#faf9f8',
            paddingBottom: '4px',
            marginTop: '-20px',
          }}
        >
          {headerAlertElements}
        </div>
      ) : null}
    </>
  );
};

export default SalesOrderHeaderComponent;
